/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */

import type { CancelTokenSource } from 'axios';

import { resolveRestApi } from '../router/app-router';
import { fetch as axiosFetch, getCancelTokenSource, isFetchCanceled } from '../service_broker/fetch';
import type { Config } from '../service_broker/fetch';
import type { C11nEnv } from '../interpreter/c11n-env';

import type {
  FileResponseObject,
  AttachmentObject,
  AttachmentProperties,
  Attachment,
  CaseLinkedAttachmentObject,
  AttachmentData,
  AttachmentValidate
} from './types';

/**
 * The AttachmentUtils module contains utility APIs to handle the attachments of a case.
 * @module AttachmentUtils
 */

const UPLOAD_ATTACHMENT = 'uploadAttachment';
const LINK_CASE_ATTACHMENT = 'linkCaseAttachment';
const GET_CASE_ATTACHMENTS = 'getCaseAttachments';
const DOWNLOAD_ATTACHMENT = 'downloadAttachment';
const EDIT_ATTACHMENT = 'editAttachment';
const DELETE_ATTACHMENT = 'deleteAttachment';
const GET_ATTACHMENTS_CATEGORIES = 'getAttachmentCategories';
const DOWNLOAD_DOCUMENT = 'downloadDocument';
const DELETE_DOCUMENT = 'deleteDocument';
const cancelSources: { [key: string]: CancelTokenSource } = {};

/**
 * Creates a cancel source, store it in an object and returns the cancel source.
 * @function
 * @static
 * @param fileID The unique ID of file to use it as a key to store the cancel source
 * @private
 * @returns cancelSource
 */
const getNewCancelSource = (fileID: string): CancelTokenSource => {
  const cancelSource = getCancelTokenSource();
  cancelSources[fileID] = cancelSource;
  return cancelSource;
};

/**
 * Cancels the ongoing upload request for a file that is being uploaded to a server.
 * @function
 * @static
 *
 * @example <caption>In this example, the API cancels the upload request for the file with the ID _xnn4f3ig1 that is being uploaded to the server.</caption>
 * PCore.getAttachmentUtils().cancelRequest('_xnn4f3ig1');
 *
 * @param fileID The unique ID of the file that is being uploaded.
 */
const cancelRequest = (fileID: string) => {
  /* istanbul ignore next */
  if (cancelSources[fileID]) {
    cancelSources[fileID].cancel();
    delete cancelSources[fileID];
  }
};

/**
 * This API uploads a file to the server.
 * @function
 * @static
 * @example <caption>In this example, the API uploads a file to the server.</caption>
 * const onUploadProgress = (id, progressInfo) => {
 *  // id is the unique ID provided in the file object
 *  // progressInfo is an object containing the progress of the file that is being uploaded
 * }
 *
 * const errorHandler = (isRequestCancelled, file) => {
 *  return (error) => {
 *
 *    // isRequestCancelled - function to determine whether the upload request is cancelled
 *    // file – the file object that was being uploaded
 *    // error - an object containing data related to the error.
 *    // Some of the sample properties of the error object in the event of a network failure are mentioned below:
 *    //  code: "ERR_NETWORK"
 *    //  message: "Network Error"
 *
 *      if (!isRequestCancelled(error)) {
 *        // your code to handle genuine errors
 *      }
 *  }
 * }
 *
 * const uniqueID = "_xnn4f3ig1";   //unique file ID generated by user
 * const file = {
 *  "lastModified" : "1716379450677",
 *  "lastModifiedDate" : "Wed May 22 2024 17:34:10 GMT+0530 (India Standard Time) "
 *  "name": "abc_resume.pdf",
 *  "size": 890428,
 *  "type": "application/pdf",
 *  "webkitRelativePath":"",
 * }
 * file.ID = uniqueID;
 * file.category = "PurchaseOrder"
 *
 * PCore.getAttachmentUtils().uploadAttachment(file, onUploadProgress, errorHandler, 'app/primary_1', 'ON8TTL-C11NGALL-WORK WMS-29004', 'Bills')
 * .then((fileResponse) => {
 * // Response Object structure
 * // ID: "UGVntX19BdHRhY2htZW50XzY2NTU5ZjIzMDI0ZjY4Nj"
 * //     ID is generated in the server when the file is uploaded successfully
 * // category: "PurchaseOrder"
 * //     category is the sub-group of the file that has been uploaded
 * // clientFileID: "_xnn4f3ig1"
 * //     clientFileID is the unique identifier of the file that has been uploaded
 * // filename: "abc_resume.pdf"
 * //     filename is the name of the file that has been uploaded
 * // type: "File"
 * //     the value of type is always File
 *
 * }).catch(err => {
 *    // Error handling
 * });
 *
 * @param file               The file that needs to be uploaded to the server.
 * @param onUploadProgress A callback function that provides the upload progress of the file.
 * @param errorHandler     A callback function to handle exceptions.
 * @param context            The name of the context where the API is being called.
 * @param contextID       (Optional) ID of the object in the context of which the upload is happening
 * @param attachmentCategory        (Optional) The attachment category of the file being uploaded. For example, 'Bills', 'Invoices', etc.
 * @param file.ID         The unique identifier of the file that must be uploaded.
 * @param file.category   (Optional) The sub-group of the file that must be uploaded.
 * @returns A Promise that resolves to an object containing the metadata of the uploaded file.
 */

const uploadAttachment = (
  file: Blob & Attachment,
  onUploadProgress: Config['onUploadProgress'],
  errorHandler: Function,
  context: string,
  contextID?: string,
  attachmentCategory?: string
): Promise<object> => {
  const formData = new FormData();
  formData.append('appendUniqueIdToFileName', 'true');
  formData.append('file', file);
  if (contextID) {
    formData.append('contextID', contextID);
  }
  if (attachmentCategory) {
    formData.append('category', attachmentCategory);
  }
  const { restApi, reqType } = resolveRestApi(UPLOAD_ATTACHMENT, {});
  return axiosFetch(
    restApi,
    {
      method: reqType,
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      body: formData,
      onUploadProgress,
      signal: getNewCancelSource(file.ID).token
    },
    context
  )
    .then((fileResponse) => ({
      type: 'File',
      category: file.category || 'File',
      ID: fileResponse.data.ID,
      clientFileID: file.ID,
      filename: file.name
    }))
    .catch(
      errorHandler
        ? errorHandler(isFetchCanceled, file)
        : (error) => {
            error.isHandled = false;
            throw error;
          }
    )
    .finally(() => delete cancelSources[file.ID]);
};

/**
 * This API links an uploaded file or an array of links to the case.
 * @function
 * @static
 * @example <caption>In this example, the API links the uploaded file whose ID is '234545' to the case whose ID is 'W-102'.</caption>
 * const file = {
 *  "type": "File",
 *  "category": "Screenshot",
 *  "attachmentFieldName": "Screenshot",
 *  "fileType": "PNG",
 *  "name": "Screenshot for this issue",
 *  "ID": "234545"
 * }
 * PCore.getAttachmentUtils().linkAttachmentsToCase('ORG-APP-WORK W-102', [file], "File", "app/primary_1")
 * .then((attachments) => {
 *   // attachments
 * }).catch(err => {
 *    // Error handling
 * });
 *
 * @example <caption>In this example, the API links multiple links to the case whose ID is 'W-102'.</caption>
 * const url1 = {"type":"URL","category":"URL","url":"https://www.google.com","name":"Google"}
 * const url2 = {"type":"URL","category":"URL","url":"https://www.firefox.com","name":"Firefox"}
 *
 * PCore.getAttachmentUtils().linkAttachmentsToCase('ORG-APP-WORK W-102', [url1, url2], "File", "app/primary_1")
 * .then((attachments) => {
 *   // attachments
 * }).catch(err => {
 *    // Error handling
 * });
 * []
 * @param caseID         The ID of the case pointing to a pzInsKey value to which the uploaded attachment needs to be linked.
 * @param attachments     A file or an array of links that need to be linked to the case.
 * @param attachmentType The type of attachments(File, URL, etc) that we are trying to link them to the case.
 * @param context        The name of the context where the API is being called.
 * @returns A Promise that links an uploaded attachment to a case.
 */
const linkAttachmentsToCase = (
  caseID: string,
  attachments: CaseLinkedAttachmentObject[],
  attachmentType: string,
  context: string
): Promise<object> => {
  const reqInfo: Pick<Config, 'signal'> = {};
  // We are linking one file at a time but multiple urls can be attached at a time
  // So, if attachmentType is File then processing one at a time
  if (attachmentType === 'File') {
    const [attachmentInfo] = attachments;
    const { clientFileID } = attachmentInfo;
    delete attachmentInfo.clientFileID;
    if (clientFileID) {
      reqInfo.signal = getNewCancelSource(clientFileID).token;
    }
  }

  const { restApi, reqType } = resolveRestApi(LINK_CASE_ATTACHMENT, { caseID });

  return axiosFetch(
    restApi,
    {
      ...reqInfo,
      body: { attachments },
      method: reqType
    },
    context
  );
};

/**
 * This API obtains the attachments linked to a case.
 * @function
 * @static
 * @example <caption>In this example, the API obtains the attachments linked to a case whose ID is W-102.</caption>
 * PCore.getAttachmentUtils().getCaseAttachments('ORG-APP-WORK W-102', 'app/primary_1')
 * .then(attachments => {
 *    // attachments array
 * }).catch(err => {
 *   // errors
 * });
 *
 * @param caseID             The ID of the case pointing to pzInskey value whose attachments must be obtained.
 * @param context            The name of the context where the API is being called.
 * @param includeThumbnail  The flag that determines if the thumbnail associated with the attachment
 *                                    is returned. The default value is false.If includeThumbnail is true, the
 *                                    thumbnail associated with the attachment is returned as a property of the
 *                                    attachment object.If includeThumbnail is false, the thumbnail associated
 *                                    with the attachment is not returned.
 * @returns A Promise for the attachments linked to a case as a Promise.
 */
const getCaseAttachments = (caseID: string, context: string, includeThumbnail = false): Promise<object> => {
  const { restApi, reqType } = resolveRestApi(GET_CASE_ATTACHMENTS, {
    caseID,
    includeThumbnail
  });

  return axiosFetch(
    restApi,
    {
      method: reqType
    },
    context
  ).then((response) => response.data.attachments);
};

/**
 * This API obtains the attachment categories linked to a case.
 * @function
 * @static
 * @example <caption>In this example, the API obtains the attachments categories linked to a case whose ID is W-102.</caption>
 * PCore.getAttachmentUtils().getAttachmentCategories('ORG-APP-WORK W-102', 'file', 'app/primary_1')
 * .then(attachments => {
 * // attachments array
 * }).catch(err => {
 * // errors
 * });
 *
 * @param caseID     The ID of the case pointing to pzInsKey value whose attachment categories must be obtained.
 * @param type       The type of attachment.
 * @param context    The name of the context where the API is being called.
 * @returns A Promise that obtains the attachment categories linked to a case.
 */
const getAttachmentCategories = (caseID: string, type: string, context: string): Promise<object> => {
  const { restApi, reqType } = resolveRestApi(GET_ATTACHMENTS_CATEGORIES, { caseID, type });

  return axiosFetch(
    restApi,
    {
      method: reqType
    },
    context
  ).then((response) => response.data);
};

/**
 * This API downloads the binary content of an attachment.
 * @function
 * @static
 * @example <caption>In this example, the API returns a Promise that obtains the binary content of the attachment whose ID is 'LINK-ATTACHMENT ON8TTL-GALLERY-WORK AT-17001!20230912T065407.556 GMT'.</caption>
 * PCore.getAttachmentUtils().downloadAttachment('LINK-ATTACHMENT ON8TTL-GALLERY-WORK AT-17001!20230912T065407.556 GMT', 'app/primary_1')
 * .then(() => {
 *    // success
 * }).catch(err => {
 *   // errors
 * });
 *
 * @param attachmentID   The ID of the attachment whose binary content needs to be downloaded.
 * @param context        The name of the context where the API is being called
 * @returns The binary content of an attachment as a Promise.
 */
const downloadAttachment = (attachmentID: string, context: string, responseEncoding: string): Promise<object> => {
  const { restApi, reqType } = resolveRestApi(DOWNLOAD_ATTACHMENT, {
    attachmentID
  });

  return axiosFetch(
    restApi,
    {
      method: reqType,
      ...(responseEncoding === 'binary' && { responseType: 'arraybuffer' })
    },
    context
  );
};

/**
 * This API obtains the absolute link of an attachment.
 * @function
 * @static
 * @example <caption>In this example, the API returns a absolute url link the attachment whose ID is 'LINK-ATTACHMENT ON8TTL-GALLERY-WORK AT-17001!20230912T065407.556 GMT'.</caption>
 * PCore.getAttachmentUtils().getAttachmentURL('LINK-ATTACHMENT ON8TTL-GALLERY-WORK AT-17001!20230912T065407.556 GMT')
 * .then(() => {
 *    // success
 * }).catch(err => {
 *   // errors
 * });
 *
 * @param attachmentID   The ID of the attachment whose absolute link needs to be obtained
 * @returns The attachment url corresponding to the attachmentID.
 */
const getAttachmentURL = (attachmentID: string) => {
  const { restApi } = resolveRestApi(DOWNLOAD_ATTACHMENT, {
    attachmentID
  });
  return restApi;
};

/**
 * This API edits an attachment from case to which it is linked.
 * @function
 * @static
 * @example <caption>In this example, he API edits the attachment Name and Category whose
 * ID is 'LINK-ATTACHMENT ON8TTL-GALLERY-WORK AT-17001!20230912T065407.556 GMT'.</caption>
 * const data = {"name":"Case Study","category":"File"};
 * PCore.getAttachmentUtils().editAttachment('LINK-ATTACHMENT ON8TTL-GALLERY-WORK AT-17001!20230912T065407.556 GMT', data, 'app/primary_1')
 * .then(() => {
 *    // success
 * }).catch(err => {
 *   // errors
 * });
 *
 * @param attachmentID                 The ID of the attachment that needs to be edited.
 * @param attachmentMetaData The Name and Category in a object format {name: "", category: ""}
 * @param context                      The name of the context where the API is being called
 * @returns A Promise that when resolved successfully edits the attachment.
 */
const editAttachment = (
  attachmentID: string,
  attachmentMetaData: Pick<Attachment, 'name' | 'category' | 'description'>,
  context: string
): Promise<object> => {
  const { restApi, reqType } = resolveRestApi(EDIT_ATTACHMENT, {
    attachmentID
  });

  return axiosFetch(
    restApi,
    {
      method: reqType,
      body: { ...attachmentMetaData }
    },
    context
  );
};

/**
 * This API deletes an attachment from case to which it is linked.
 * @function
 * @static
 * @example <caption>In this example, he API deletes the attachment whose
 * ID is 'LINK-ATTACHMENT ON8TTL-GALLERY-WORK AT-17001!20230912T065407.556 GMT'.</caption>
 * PCore.getAttachmentUtils().deleteAttachment('LINK-ATTACHMENT ON8TTL-GALLERY-WORK AT-17001!20230912T070131.983 GMT', 'app/primary_1')
 * .then(() => {
 *    // success
 * }).catch(err => {
 *   // errors
 * });
 *
 * @param attachmentID   The ID of the attachment that needs to be deleted.
 * @param context        The name of the context from where the API is being called
 */
const deleteAttachment = (attachmentID: string, context: string) => {
  const { restApi, reqType } = resolveRestApi(DELETE_ATTACHMENT, {
    attachmentID
  });

  return axiosFetch(
    restApi,
    {
      method: reqType
    },
    context
  );
};

/**
 * This API downloads the binary content of an document.
 * @function
 * @private
 * @static
 * @example <caption>In this example, the API returns a Promise that obtains the binary content of the document whose ID is 'PEGASOCIAL-DOCUMENT DOC-30488'.</caption>
 * PCore.getAttachmentUtils().downloadDocument('PEGASOCIAL-DOCUMENT DOC-30488', 'app/primary_1')
 * .then(() => {
 *    // success
 * }).catch(err => {
 *   // errors
 * });
 *
 * @param documentID   The ID of the document whose binary content needs to be downloaded.
 * @param context      The name of the context where the API is being called
 * @returns A Promise that obtains the binary content of a document.
 */
const downloadDocument = (documentID: string, context: string): Promise<object> => {
  const { restApi, reqType } = resolveRestApi(DOWNLOAD_DOCUMENT, {
    documentID
  });

  return axiosFetch(
    restApi,
    {
      method: reqType
    },
    context
  );
};

/**
 * This API deletes an document from case to which it is linked.
 * @function
 * @private
 * @static
 * @example <caption>In this example, the API deletes the document whose ID is 'PEGASOCIAL-DOCUMENT DOC-30488' from case 'C-1'.</caption>
 * PCore.getAttachmentUtils().deleteDocument('ORG-APP-WORK C-1', 'PEGASOCIAL-DOCUMENT DOC-30488', 'app/primary_1')
 * .then(() => {
 *    // success
 * }).catch(err => {
 *   // errors
 * });
 *
 * @param caseID     The ID of the case pointing to pzInsKey value.
 * @param documentID The ID of the document that needs to be deleted.
 * @param context    The name of the context where the API is being called.
 */
const deleteDocument = (caseID: string, documentID: string, context: string) => {
  const { restApi, reqType } = resolveRestApi(DELETE_DOCUMENT, {
    caseID,
    documentID
  });

  return axiosFetch(
    restApi,
    {
      method: reqType
    },
    context
  );
};

/**
 * Obtains the URL for opening documents in the Pega Infinity portal.
 * @function
 * @private
 * @static
 * @example <caption>In this example, the API get the URL for documents.</caption>
 * PCore.getAttachmentUtils().getDocumentURL(getPConnect())
 * .then(() => {
 *    // success
 * }).catch(err => {
 *   // errors
 * });
 *
 * @param pConnectObj   The pConnect object of the component from where the API is being called.
 * @returns A Promise that returns the URL for documents.
 */
const getDocumentURL = (pConnectObj: C11nEnv) => {
  const dataViewName = PCore.getEnvironmentInfo().getDocumentUrlDP() || 'D_pxGetDocumentURL';
  return PCore.getDataPageUtils()
    .getPageDataAsync(dataViewName, pConnectObj.getContextName(), {}, { invalidateCache: true })
    .catch((error: { response: string }) => {
      pConnectObj.reportError(': Error occurred during ajax call getDocumentURL API : ', error.response);
    });
};

const getIconFromFileType = (fileType: string) => {
  let icon = 'document-doc';
  if (!fileType) {
    return icon;
  }
  if (fileType.startsWith('audio')) {
    icon = 'audio';
  } else if (fileType.startsWith('video')) {
    icon = 'video';
  } else if (fileType.startsWith('image')) {
    icon = 'picture';
  } else if (fileType.includes('pdf')) {
    icon = 'document-pdf';
  } else {
    const [, subtype] = fileType.split('/');
    const foundMatch = (sources: string[]) => sources.some((key: string) => subtype.includes(key));

    if (foundMatch(['excel', 'spreadsheet'])) {
      icon = 'document-xls';
    } else if (foundMatch(['zip', 'compressed', 'gzip', 'rar', 'tar'])) {
      icon = 'document-compress';
    }
  }

  return icon;
};

/**
 * Transform attachment response properties.
 *
 * @function
 * @private
 * @static
 * @example <caption>In this example, the API transforms the attachment response properties into new response structure.</caption>
 * PCore.getAttachmentUtils().buildFilePropsFromResponse(FileResponseObject)
 * .then(() => {
 *    // success
 * }).catch(err => {
 *   // errors
 * });
 *
 * @param respObj The FileResponseObject object for which mapping need to be done.
 * @returns A new FileResponseObject would be returned.
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
const buildFilePropsFromResponse = (respObj: FileResponseObject) => {
  // introduced as part of BUG-849659 :
  // pyCategoryLabel : Stores the pyLabel of the attachment category rule from class "Rule-Obj-AttachmentCategory"
  // pxCreateOpName: Full name of the operator who created the attachment.
  let categoryName;
  if (respObj.pyCategoryLabel) {
    categoryName = `${respObj.pyCategoryLabel},`;
  } else {
    categoryName = respObj.pyCategoryName ? `${respObj.pyCategoryName},` : respObj.Category;
  }
  let createOperatorName;
  if (respObj.pxCreateOpName) {
    createOperatorName = respObj.pxCreateOpName;
  } else {
    createOperatorName = respObj.pxCreateOperator ? respObj.pxCreateOperator : respObj.CreateUser?.Name || '';
  }
  // infinity Example: { "pyFileName": "filename", "pyAttachName":"filename.png" }
  // extension value doesn't come separately in infinity
  // LP example: { "FileName": "filename.png", "FileExtension": "png" }
  let fileName;
  if (respObj.pyFileName) {
    fileName = respObj.pyFileName;
  } else {
    fileName =
      respObj.FileExtension && respObj.FileExtension !== '' && respObj.FileName.endsWith(respObj.FileExtension)
        ? respObj.FileName.substring(0, respObj.FileName.lastIndexOf(respObj.FileExtension) - 1)
        : respObj.FileName;
  }
  let fileExtension;
  if (respObj.pyAttachName && respObj.pyFileName) {
    fileExtension =
      respObj.pyAttachName.startsWith(respObj.pyFileName) &&
      respObj.pyAttachName.split('.').length - 1 === respObj.pyFileName.split('.').length
        ? respObj.pyAttachName.split('.').pop()
        : '';
  } else {
    fileExtension = respObj.FileExtension;
  }

  return {
    props: {
      meta: `${categoryName} ${createOperatorName}`,
      name: respObj.pyAttachName ? respObj.pyAttachName : respObj.FileName,
      icon: getIconFromFileType(respObj.pyMimeFileExtension || respObj.MimeType),
      error: respObj.error || null,
      thumbnail: respObj.thumbnail || null
    },
    responseProps: {
      fileName: respObj.pyAttachName ? respObj.pyAttachName : respObj.FileName,
      name: fileName,
      category: respObj.pyCategoryName ? respObj.pyCategoryName : respObj.Category,
      extension: fileExtension,
      ID: respObj.pzInsKey ? respObj.pzInsKey : respObj.ID,
      mimeType: respObj.pyMimeFileExtension ? respObj.pyMimeFileExtension : respObj.MimeType,
      attachmentURL: respObj.pyAttachmentLink ? respObj.pyAttachmentLink : respObj.AttachmentURL,
      createUser: createOperatorName,
      createDateTime: respObj.pxCreateDateTime ? respObj.pxCreateDateTime : respObj.CreateDateTime,
      responseType: respObj.responseType ?? (respObj.ID ? 'binary' : null),
      localAttachment: !!respObj.localAttachment
    }
  };
};

/**
 * Prepare attachment data compatible with attachment DX Component
 *
 * @function
 * @private
 * @static
 * @example <caption>In this example, the API transforms the attachment response into predefined object structure.</caption>
 * const { hasUploadedFiles, attachments, categoryName } = PCore.getAttachmentUtils().prepareAttachmentData(attachmentResponse)
 *
 * @param attachmentResponse   The attachmentResponse object which gets modified into another Attachment DX Component understandable object.
 * @returns A new attachment object would be returned.
 */
const prepareAttachmentData = (attachmentResponse: AttachmentObject): AttachmentData => {
  let attachments: AttachmentProperties[] = [];
  let isOldAttachment = false;
  if (attachmentResponse && Object.hasOwn(attachmentResponse, 'pyCount')) {
    isOldAttachment = true;
    attachments =
      attachmentResponse?.pxResults
        ?.filter((file: FileResponseObject) => !!file.pzInsKey)
        .map((file: FileResponseObject) => buildFilePropsFromResponse(file)) || [];
  } else if (attachmentResponse && typeof attachmentResponse === 'object') {
    let attachmentList;
    if (Array.isArray(attachmentResponse)) {
      attachmentList = attachmentResponse;
    } else {
      attachmentList = [attachmentResponse];
    }

    attachments = attachmentList
      ?.filter((file: FileResponseObject) => (file.ID && file.FileName) || file.pzInsKey)
      .map(buildFilePropsFromResponse);
  } else {
    attachments = [];
  }

  return {
    hasUploadedFiles: attachments.length > 0,
    categoryName: attachmentResponse?.pyCategoryName || attachments?.[0]?.responseProps?.category || '',
    attachments,
    isOldAttachment
  };
};

/**
 * Validate attachment data for required validation
 *
 * @function
 * @private
 * @static
 * @param value The attachment redux object.
 * @param contextTreeNodeValue context tree object
 * @returns Empty object or redux value will be returned.
 */
const validateAttachments = (value: AttachmentValidate, contextTreeNodeValue: AttachmentData | AttachmentObject) => {
  let newValue: string | AttachmentValidate = value;
  if (typeof contextTreeNodeValue === 'object' && 'attachments' in contextTreeNodeValue) {
    const { attachments }: { attachments: AttachmentProperties[] } = contextTreeNodeValue as AttachmentData;

    if (attachments.length === 0) {
      newValue = '';
    }
  } else {
    // Maintain backward compatibility for old attachments
    const { attachments }: { attachments: AttachmentProperties[] } = prepareAttachmentData(
      contextTreeNodeValue as AttachmentObject
    );
    if (attachments.length === 0) {
      newValue = '';
    }
  }
  return newValue;
};

export default {
  uploadAttachment,
  linkAttachmentsToCase,
  getCaseAttachments,
  getAttachmentCategories,
  downloadAttachment,
  editAttachment,
  deleteAttachment,
  downloadDocument,
  deleteDocument,
  getDocumentURL,
  cancelRequest,
  buildFilePropsFromResponse,
  getAttachmentURL,
  prepareAttachmentData,
  validateAttachments
};
